import { Navigate, Routes, Route } from "react-router";
import { Sell } from "./pages/Sell";
import { Settings } from "./pages/Settings";
import { Tickets } from "./pages/Tickets";
import { Comandas } from "./pages/Comandas";
import { useContext, useState, useEffect, useRef } from "react";
import { UserContext } from "./context/user";
import { Icon } from "@iconify/react";
import dayjs from "dayjs";
import { Get, Put } from "./api/api";
import { heartbeatDevice, DEVICE_CHANGED_EVENT, DEVICE_SHOP_ID_KEY, DEVICE_STATUS_CHANGED_EVENT, DEVICE_TOKEN_KEY } from "./api/device";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { LazyLoadImage } from "react-lazy-load-image-component";
import logo from "./assets/logo_blanco.png";
import { useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { printComanda } from "./api/printer";
import { GenerateComanda } from "./api/ticket";

function App() {
  const { shop } = useContext(UserContext);

  useDeviceHeartbeat();

  if (!localStorage.getItem("shop_id")) {
    return <Navigate to="/shopselect" />;
  }

  if (!localStorage.getItem("exercise_id")) {
    return <Navigate to="/shopselect" />;
  }

  return (
    <div className="absolute top-0 left-0 right-0 bottom-0">
      <div className="animate-fade-down animate-once absolute h-10 px-4 left-0 right-0 bg-black text-white font-semibold flex items-center justify-between">
        <div className="flex gap-2 items-center">
          <div>{shop.name}</div>
        </div>
        <LazyLoadImage src={logo} className="h-8 absolute left-1/2 -translate-x-1/2" />
        <div className="flex items-center gap-6">
          <Clock />
          <Notifications />
          <Connection />
          <Fullscreen />
        </div>
      </div>
      <div data-bottom-nav-scroll className="absolute left-0 right-0 bottom-0 top-10 overflow-y-auto">
        <Routes>
          <Route path="/" element={<Sell />} />
          <Route path="settings" element={<Settings />} />
          <Route path="tickets" element={<Tickets />} />
          <Route path="comandas" element={<Comandas />} />
        </Routes>
      </div>
    </div>
  );
}

function useDeviceHeartbeat() {
  useEffect(() => {
    const sendHeartbeat = () => {
      const token = localStorage.getItem(DEVICE_TOKEN_KEY);
      const linkedShopId = localStorage.getItem(DEVICE_SHOP_ID_KEY);
      const currentShopId = localStorage.getItem("shop_id");

      if (!token || !linkedShopId || linkedShopId !== currentShopId) return;

      void heartbeatDevice(token).then((result) => {
        window.dispatchEvent(new CustomEvent(DEVICE_STATUS_CHANGED_EVENT, { detail: result }));

        if (result._status === 401) {
          localStorage.removeItem(DEVICE_TOKEN_KEY);
          localStorage.removeItem(DEVICE_SHOP_ID_KEY);
          window.dispatchEvent(new Event(DEVICE_CHANGED_EVENT));
        }
      });
    };

    sendHeartbeat();
    const interval = window.setInterval(sendHeartbeat, 30000);
    window.addEventListener(DEVICE_CHANGED_EVENT, sendHeartbeat);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener(DEVICE_CHANGED_EVENT, sendHeartbeat);
    };
  }, []);
}

function parseComandaCart(value) {
  try {
    const parsed = JSON.parse(value || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function Notifications() {
  const { shop } = useContext(UserContext);
  const { data: notifications, isLoading, refetch } = useQuery({
    queryKey: ['notifications', localStorage.getItem('shop_id')],
    queryFn: () => Get({ request: '/notifications?shop_id=' + localStorage.getItem('shop_id') }),
    refetchInterval: 5000,
    placeholderData: []
  });

  // Las notificaciones se releen cada 5s: sin este guarda, una comanda puede
  // imprimirse dos veces si el sondeo vuelve antes de terminar la anterior.
  const printingComandas = useRef(new Set());

  useEffect(() => {
    notifications.map(async notification => {
      // El backend guarda `print: true` (boolean), no string "print"
      if (notification.type !== 'comanda' || notification.print !== true) return;
      if (printingComandas.current.has(notification.id)) return;
      printingComandas.current.add(notification.id);

      try {
        const comanda = await Get({ request: '/comandas/' + notification.type_id + '?shop_id=' + localStorage.getItem('shop_id') });
        if (!comanda.success) {
          console.warn('comanda: no se pudo leer', notification.type_id, comanda);
          return;
        }

        const cart = parseComandaCart(comanda.comanda?.cart);
        if (cart.length === 0) {
          console.warn('comanda: el carrito viene vacío', notification.type_id, comanda.comanda?.cart);
          return;
        }
        comanda.comanda.cart = cart;

        // La comanda entra directa a la impresora de cocina. Solo se avisa al
        // backend cuando el papel ha salido, así una comanda que no se pudo
        // imprimir se reintenta en el siguiente sondeo.
        const toPrint = GenerateComanda(comanda.comanda, shop?.ticket_settings?.paper_saving || false);
        console.log('comanda automática: data a imprimir', {
          id: comanda.comanda?.id,
          name: comanda.comanda?.name,
          cartLen: comanda.comanda?.cart?.length,
          paperSaving: shop?.ticket_settings?.paper_saving || false,
        });
        const printed = await printComanda(toPrint);
        console.log('comanda automática: impresión', notification.id, printed ? 'enviada' : 'fallida');

        if (printed) {
          await Put({
            request: '/notifications/' + notification.id + '?shop_id=' + localStorage.getItem('shop_id'),
            data: { print: 0 }
          });
        }
      } catch (error) {
        // Antes una excepción aquí —por ejemplo un `comanda` que venga sin
        // relaciones— se comía la impresión entera sin dejar rastro.
        console.error('comanda: fallo al imprimir', notification.id, error);
      } finally {
        printingComandas.current.delete(notification.id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications]);

  if (isLoading) {
    return <div className="flex items-center justify-center p-1"><Icon icon="mdi:bell" /></div>;
  }

  return (
    <div className="relative">
      <div hidden={notifications.length == 0} className="h-2 w-2 bg-blue-600 rounded-full absolute right-0 top-0">
        <div className="animate-ping w-full h-full bg-blue-600 rounded-full"></div>
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <div className="flex items-center justify-center p-1 cursor-pointer">
            <Icon icon="mdi:bell" />
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-96">
          {notifications.length == 0 ? (
            <DropdownMenuItem disabled>No hay notificaciones</DropdownMenuItem>
          ) : (
            notifications.map(notification => (
              <DropdownMenuItem key={notification.id} className="p-0" onSelect={e => e.preventDefault()}>
                <Notification notification={notification} refetch={refetch} />
              </DropdownMenuItem>
            ))
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function Notification({ notification, refetch }) {
  const [deleted, setDeleted] = useState(false);

  return (
    <div className="w-full flex justify-between p-2">
      <div className="flex gap-1 items-center">
        <div className="w-6 text-neutral-400">
          <Icon icon={notification.icon} />
        </div>
        <div>
          <div className="text-sm font-semibold">{notification.title}</div>
          <div className="text-xs">{notification.body}</div>
        </div>
      </div>
      <div
        onClick={async (e) => {
          e.stopPropagation();
          setDeleted(true);
          await Put({
            request: '/notifications/' + notification.id + '?shop_id=' + localStorage.getItem('shop_id'),
            data: { read: 1 }
          });
          toast.success('Notificación marcada como leída');
          refetch();
        }}
        className="flex items-center justify-center h-full aspect-square border rounded-md bg-white hover:bg-blue-50 hover:text-blue-600 cursor-pointer"
      >
        <Icon icon={deleted ? 'eos-icons:three-dots-loading' : 'mdi:check'} />
      </div>
    </div>
  );
}

async function getPing() {
  let init = Date.now();
  await Get({ request: "/ping" });
  let end = Date.now();
  return end - init;
}

function Connection() {
  const [icon, setIcon] = useState("eos-icons:three-dots-loading");
  const [ping, setPing] = useState("Conectando...");

  useEffect(() => {
    let i = setInterval(async () => {
      let pi = await getPing();
      setPing(pi + 'ms');
      if (pi > 5000) setIcon("mdi:wifi-strength-alert-outline");
      if (pi < 4000) setIcon("mdi:wifi-strength-1");
      if (pi < 3000) setIcon("mdi:wifi-strength-2");
      if (pi < 2000) setIcon("mdi:wifi-strength-3");
      if (pi < 1000) setIcon("mdi:wifi-strength-4");
    }, 5000);
    return () => clearInterval(i);
  }, []);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div><Icon icon={icon} /></div>
        </TooltipTrigger>
        <TooltipContent>{ping}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function Clock() {
  const [time, setTime] = useState(dayjs().format("HH:mm"));

  useEffect(() => {
    const i = setInterval(() => {
      setTime(dayjs().format("HH:mm"));
    }, 1000);
    return () => clearInterval(i);
  }, []);

  return <div>{time}</div>;
}

function Fullscreen() {
  const [isFullscreen, setIsFullscreen] = useState(!!document.fullscreenElement);

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  async function toggleFullscreen() {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await document.documentElement.requestFullscreen();
      }
    } catch {
      toast.error("No se ha podido cambiar el modo de pantalla");
    }
  }

  const label = isFullscreen ? "Salir de pantalla completa" : "Pantalla completa";

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={toggleFullscreen}
            aria-label={label}
            className="flex items-center justify-center p-1 cursor-pointer"
          >
            <Icon icon={isFullscreen ? "mdi:fullscreen-exit" : "mdi:fullscreen"} />
          </button>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default App;
